import { Map, Record } from 'immutable';

import type { Logout } from 'app/lib/auth/types';

import { StakeType } from 'app/lib/staking/constants';
import { logout } from 'app/redux/actions/auth';
import type { InitAction } from 'app/redux/actions/init';

import {
  CheckAvailabilitySuccessPayload,
  CLEANUP, Cleanup,
  CLEAR_BETS_INFO_ERROR, ClearBetsInfoError,
  REFRESH_AVAILABILITY, RefreshAvailability,
} from '../../actions';
import {
  CHANGE_ENTRY_AMOUNT, ChangeEntryAmount,
  COMPLETE_REPLACE, CompleteReplace,
  DELETE_FROM_BET_SLIP, DeleteFromBetSlip,
  INSERT_FAILED_ENTRY_IN_BET_SLIP, INSERT_IN_BET_SLIP, InsertFailedEntryInBetSlip,
  InsertInBetSlip,
  SETUP_ENTRY_AMOUNT, SetupEntryAmount,
  SWITCH_TO_TOTAL_AMOUNT, SwitchToTotalAmount,
} from '../../actions/entries';
import {
  REMOVE_FREE_BET, REMOVE_PLEDGE_STAKE, RemoveFreeBet,
  RemovePledgeStake,
  SET_FREE_BET, SET_PLEDGE_STAKE, SetFreeBet,
  SetPledgeStake,
} from '../../actions/paymentOptions';
import { CHANGE_STAKE_TYPE_AMOUNT, ChangeStakeTypeAmount, UPDATE_CHECKED_STAKE_TYPE } from '../../actions/stakeTypes';
import {
  FAILURE_SUBMISSION_END, FailureSubmissionEnd,
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_REQUEST, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeRequest,
  SubmitStakeSuccess,
} from '../../actions/submission';
import { AccountType, AmountInputModifier, SubmissionState } from '../../constants';
import { BetInfo, RecordOfBetInfoType, RecordOfEntryErrorType } from '../../records';
import {
  isOnlyAcceptAmount,
  selectSingleAmountEntry,
} from '../../selectors/stakeTypes/single';
import { RecordOfSingleStakeType, SingleStakeTypeState } from '../../types/stakeTypes/singles';
import { setupEntryError } from '../../utils/stake-types-reducers';

// ---

export const initialState = Record<SingleStakeTypeState>({
  amountModifier: AmountInputModifier.DIVIDE,
  betsInfo: Map(),
}, 'singleStakeType');

export type SingleStakeTypeActions =
  | InitAction
  | InsertInBetSlip
  | InsertFailedEntryInBetSlip
  | DeleteFromBetSlip
  | CompleteReplace
  | ChangeEntryAmount
  | SetupEntryAmount
  | ChangeStakeTypeAmount
  | SubmitStakeRequest
  | SubmitStakeSuccess
  | SubmitStakeFailure
  | FailureSubmissionEnd
  | SwitchToTotalAmount
  | RefreshAvailability
  | SetFreeBet
  | RemoveFreeBet
  | SetPledgeStake
  | RemovePledgeStake
  | ClearBetsInfoError
  | Cleanup
  | Logout;

// ---

type InsertPayload =
  | InsertInBetSlip['payload']
  | InsertFailedEntryInBetSlip['payload'];

function insertInBetSlip(
  state: RecordOfSingleStakeType,
  payload: InsertPayload,
  singles?: CheckAvailabilitySuccessPayload['singles'],
): RecordOfSingleStakeType {
  const entryId = payload.bet.entity.id;
  const matchId = payload.bet.entity.matchId;

  const bets = state.get('betsInfo');

  if (bets.has(entryId)) {
    return state;
  }

  const limits = singles?.limits.get(entryId);
  const paymentOptions = singles?.paymentOptions.get(entryId);

  const betInfo = BetInfo(payload.betInfo).merge({
    id: entryId,
    matchId,
    paymentOptions,
    limits,
  });

  state = state.set('betsInfo', bets.set(entryId, betInfo));

  return state;
}

function handleInsertFailedEntryInBetSlip(
  state: RecordOfSingleStakeType,
  error: InsertFailedEntryInBetSlip['error'],
  payload: InsertFailedEntryInBetSlip['payload'],
): RecordOfSingleStakeType {
  state = insertInBetSlip(state, payload);

  const betsInfo = state.get('betsInfo');

  return state.set(
    'betsInfo',
    betsInfo.map((info) => {
      const isMatchError = payload.matchErrors?.find((error) => error.matchId === info.matchId);

      if (!isMatchError) {
        const isBetError = payload.betErrors?.find((error) => error.betId === info.id);

        if (!isBetError) {
          return info;
        }
      }

      return setupEntryError(info, error, payload);
    }),
  );
}

function deleteFromBetSlip(
  state: RecordOfSingleStakeType,
  payload: DeleteFromBetSlip['payload'],
): RecordOfSingleStakeType {
  let bets = state.get('betsInfo');

  payload.entries.forEach(({ betId }) => {
    bets = bets.delete(betId);
  });

  if (bets.size === 1) {
    const firstInfo: RecordOfBetInfoType = bets.first();
    const infoAmount = firstInfo.amount;

    if (infoAmount) {
      state = state.set('amountModifier', AmountInputModifier.DIVIDE);
    }
  }

  return state.update('betsInfo', () => bets);
}

function removeSelectedFreebets(state: RecordOfSingleStakeType) {
  let bets = state.get('betsInfo');
  bets.forEach(({ id }) => {
    bets = bets.deleteIn([id, 'freeBet']);
    bets = bets.setIn([id, 'amount', 'account'], AccountType.MAIN_ACCOUNT);
    bets = bets.setIn([id, 'amount', 'value'], '');
  });

  return state.set('betsInfo', bets);
}

// ---

function changeEntryAmount(
  state: RecordOfSingleStakeType,
  { betId, value, account }: ChangeEntryAmount['payload'],
): RecordOfSingleStakeType {
  const amount = state.get('betsInfo')?.get(betId)?.get('amount');
  if (!amount) {
    console.error('BetSlip > reducer > stakeTypes > single > changeEntryAmount: `amount` not found');
    return state;
  }
  const updatedAmount = amount.merge({ selector: null, value, account });
  return state.setIn(['betsInfo', betId, 'amount'], updatedAmount);
}

function needResetEntryError(error?: RecordOfEntryErrorType | null) {
  return error && !isOnlyAcceptAmount(error);
}

function handleChangeEntryAmount(
  state: RecordOfSingleStakeType,
  payload: ChangeEntryAmount['payload'],
): RecordOfSingleStakeType {
  let nextState = changeEntryAmount(state, payload);

  if (state === nextState) {
    return state;
  }
  const errorByBetId = state.betsInfo.get(payload.betId)?.error;

  if (needResetEntryError(errorByBetId)) {
    nextState = resetEntryErrors(nextState);
  }

  return nextState;
}

// --- SUBMIT_STAKE

function handleSubmitStakeRequest(
  state: RecordOfSingleStakeType,
  payload: SubmitStakeRequest['payload'],
): RecordOfSingleStakeType {
  return state.set(
    'betsInfo',
    payload.bets.reduce((betsInfo, { id }) => {
      const info = betsInfo.get(id);

      if (info) {
        betsInfo = betsInfo.set(
          id,
          info
            .set('submission', SubmissionState.PROCESSING)
            .set('dialogId', payload.dialogId || ''),
        );
      }

      return betsInfo;
    }, state.get('betsInfo')),
  );
}

function handleSubmitStakeSuccess(
  state: RecordOfSingleStakeType,
  { requestPayload }: SubmitStakeSuccess['payload'],
): RecordOfSingleStakeType {
  if (!requestPayload) {
    return state;
  }

  return state.set(
    'betsInfo',
    requestPayload.bets.reduce((betsInfo, { id }) => {
      const info = betsInfo.get(id);

      if (!info) {
        return betsInfo;
      }

      return betsInfo.set(id, info.set('submission', SubmissionState.ACCEPTED));
    }, state.get('betsInfo')),
  );
}

function handleSubmitStakeFailure(
  state: RecordOfSingleStakeType,
  error: SubmitStakeFailure['error'],
  payload: SubmitStakeFailure['payload'],
): RecordOfSingleStakeType {
  if (!payload.requestPayload) {
    return state;
  }

  const matchIdsWithError = new Set();
  const betIdsWithError = new Set();

  let bets = payload.requestPayload.bets;

  const isGeneralError = !payload.matchErrors && !payload.betErrors;

  if (!isGeneralError) {
    payload.matchErrors?.forEach((match) => {
      matchIdsWithError.add(match.matchId);
    });

    payload.betErrors?.forEach((bet) => {
      betIdsWithError.add(bet.betId);
    });

    bets = payload.requestPayload.bets.filter((bet) => (
      betIdsWithError.has(bet.id) || matchIdsWithError.has(bet.matchId)
    ));
  }

  return state.set(
    'betsInfo',
    bets.reduce((betsInfo, { id }) => {
      const info = betsInfo.get(id);

      if (!info) {
        return betsInfo;
      }

      return betsInfo.set(id, setupEntryError(info, error, payload));
    }, state.get('betsInfo')),
  );
}

function handleSubmissionEnd(
  state: RecordOfSingleStakeType,
  betIds: number[],
) {
  return state.set(
    'betsInfo',
    betIds.reduce((betsInfo, id) => {
      const info = betsInfo.get(id);

      if (!info) {
        return betsInfo;
      }

      return betsInfo.setIn([id, 'submission'], SubmissionState.AWAITING);
    }, state.get('betsInfo')),
  );
}

// ---

function handleSwitchToTotalAmount(
  state: RecordOfSingleStakeType,
  payload: SwitchToTotalAmount['payload'],
): RecordOfSingleStakeType {
  const isActive = payload.value;

  state = state.update('betsInfo', (betsInfo) => (
    betsInfo.map((info) => {
      return info.setIn(['amount', 'selector'], isActive ? selectSingleAmountEntry : null);
    })
  ));

  if (!isActive) {
    state = state.set('amountModifier', AmountInputModifier.DIVIDE);
  }

  return state;
}

// ---

function refreshAvailability(
  state: RecordOfSingleStakeType,
  singles: CheckAvailabilitySuccessPayload['singles'],
): RecordOfSingleStakeType {
  let betsInfo = state.get('betsInfo');

  if (!betsInfo) {
    return state;
  }

  singles.limits.forEach((limits, betId) => {
    const betLimits = betsInfo.get(betId)?.get('limits');
    if (!betLimits) {
      console.error('BetSlip > reducer > stakeTypes > single > refreshAvailability: `betLimits` not found');
      return;
    }
    betsInfo = betsInfo.setIn([betId, 'limits'], betLimits.merge(limits));
  });

  return state.set('betsInfo', betsInfo);
}

// ---

function resetEntryErrors(
  state: RecordOfSingleStakeType,
  skipEntryIds: number[] = [],
): RecordOfSingleStakeType {
  return state.update('betsInfo', (betsInfo) => (
    betsInfo.map((info, betId) => {
      if (skipEntryIds.includes(betId)) {
        return info;
      }

      if (
        info.submission === SubmissionState.REJECTED
        || info.submission === SubmissionState.FAILED
      ) {
        info = info.set('submission', SubmissionState.AWAITING);
      }

      return info.set('error', null);
    })
  ));
}

function clearBetsInfoErrorByBetIds(
  state: RecordOfSingleStakeType,
  betIds: number[],
) {
  return state.update('betsInfo', (betsInfo) => (
    betsInfo.map((info, betId) => {
      if (!betIds.includes(betId)) {
        return info;
      }

      return info.set('error', null);
    })
  ));
}

// ---

export default function (
  state = initialState(),
  action: SingleStakeTypeActions,
): RecordOfSingleStakeType {
  switch (action.type) {
    case INSERT_IN_BET_SLIP:
      return insertInBetSlip(state, action.payload, action.payload.singles);

    case INSERT_FAILED_ENTRY_IN_BET_SLIP:
      return handleInsertFailedEntryInBetSlip(state, action.error, action.payload);

    case DELETE_FROM_BET_SLIP:
      return deleteFromBetSlip(state, action.payload);

    case COMPLETE_REPLACE:
      state = deleteFromBetSlip(state, action.payload);
      return insertInBetSlip(state, action.payload.by, action.payload.singles);

    case SETUP_ENTRY_AMOUNT:
      return changeEntryAmount(state, action.payload);

    case CHANGE_ENTRY_AMOUNT:
      return handleChangeEntryAmount(state, action.payload);

    case UPDATE_CHECKED_STAKE_TYPE:
      return resetEntryErrors(state);

    case CHANGE_STAKE_TYPE_AMOUNT: {
      const { modifier, stakeType } = action.payload;

      if (modifier !== null && stakeType.has(StakeType.SINGLE)) {
        state = state.set('amountModifier', modifier);
      }

      const betsInfoError = state.betsInfo.last()?.error;

      if (needResetEntryError(betsInfoError)) {
        return resetEntryErrors(state);
      }

      return state;
    }

    case SUBMIT_STAKE_REQUEST:
      return handleSubmitStakeRequest(state, action.payload);

    case SUBMIT_STAKE_SUCCESS: {
      if (action.payload.isBigStake) {
        return state;
      }

      return handleSubmitStakeSuccess(state, action.payload);
    }

    case SUBMIT_STAKE_FAILURE: {
      if (action.payload.isBigStake) {
        return state;
      }

      return handleSubmitStakeFailure(state, action.error, action.payload);
    }

    case FAILURE_SUBMISSION_END:
      if (!action.payload.betIds.length) {
        return state;
      }

      return handleSubmissionEnd(state, action.payload.betIds);

    case SWITCH_TO_TOTAL_AMOUNT:
      return handleSwitchToTotalAmount(state, action.payload);

    case REFRESH_AVAILABILITY:
      return refreshAvailability(state, action.payload.singles);

    case SET_FREE_BET: {
      const { betId, entry } = action.payload;

      return state.updateIn(['betsInfo', betId, 'freeBet'], () => entry);
    }

    case REMOVE_FREE_BET:
      return state.deleteIn(['betsInfo', action.payload.betId, 'freeBet']);

    case SET_PLEDGE_STAKE: {
      const { betId, entry } = action.payload;

      return state.updateIn(['betsInfo', betId, 'pledgeStake'], () => entry);
    }

    case REMOVE_PLEDGE_STAKE:
      return state.deleteIn(['betsInfo', action.payload.betId, 'pledgeStake']);

    case logout.pending.toString():
      return removeSelectedFreebets(state);

    case CLEAR_BETS_INFO_ERROR:
      return clearBetsInfoErrorByBetIds(state, action.payload.betIds);

    case CLEANUP:
      return initialState();

    default:
      return state;
  }
}
